import { alpha } from '@mui/material';
import { grey } from '@mui/material/colors';
import { ACCENT, ACCENT_HOVER } from './accent';

/**
 * The surfaces and radii of the redesign, in one place so the MUI theme and
 * `useOrderTheme` cannot drift apart. The theme applies them to everything MUI
 * renders; the hook exposes them for the surfaces MUI cannot reach — page
 * backgrounds and the `Box`-drawn cards.
 */
export const CONTROL_RADIUS = '10px';
export const CARD_RADIUS = '14px';
export const CONTROL_SIZE = 38;

export const surfaces = (dark: boolean) => ({
  pageBg: dark ? '#171717' : '#F1F2F6',
  surface: dark ? '#1E1E1E' : '#FCFCFC',
  line: dark ? 'rgba(255,255,255,0.12)' : '#E3E4E8',
  headerBg: dark ? 'rgba(255,255,255,0.04)' : '#F6F7F9',
  cardShadow: dark
    ? '0 1px 3px rgba(0,0,0,0.4)'
    : '2px 3px 8px rgba(16,24,40,0.05)',
});

export const ROW_HOVER_ALPHA = 0.1;
export const ROW_SELECTED_ALPHA = 0.18;
export const TABLE_ACCENT = ACCENT;

/**
 * Every design value the screens read, resolved per light/dark mode. This is
 * installed on the MUI theme as `palette.dash`, so a component reads it with
 * MUI's own `useTheme()` rather than a bespoke hook.
 */
export const dashTokens = (dark: boolean) => ({
  // Page & cards
  ...surfaces(dark),
  cardRadius: CARD_RADIUS,

  // Chips (brand / source pills inside product rows)
  chipBg: dark ? 'rgba(255,255,255,0.08)' : '#EDEEF3',
  chipText: dark ? grey[200] : '#35363B',

  // "OUT" outlined button
  outBorder: dark ? 'rgba(255,255,255,0.23)' : '#DFDFDF',
  outText: dark ? grey[300] : '#353535',

  // Filter card (brand / status pills, dropdown)
  filterSurface: dark ? '#1E1E1E' : '#FFFFFF',
  filterLine: dark ? 'rgba(255,255,255,0.12)' : '#E6E7EB',
  filterHover: dark ? 'rgba(255,255,255,0.06)' : '#F6F7F9',

  // Order id link colour
  orderId: dark ? '#6FB0F2' : '#1C6DC9',

  // Accent (indigo) — primary action colour across both screens
  accent: ACCENT,
  accentHover: ACCENT_HOVER,
  accentText: '#FFFFFF',

  // Shared control sizing (buttons, dropdowns, inputs)
  controlSize: CONTROL_SIZE,
  controlRadius: CONTROL_RADIUS,
  controlLine: dark ? 'rgba(255,255,255,0.18)' : '#D7D7DB',
  disabledBg: dark ? 'rgba(255,255,255,0.08)' : '#E7E7EE',
  // A disabled button still has to say what it would do. The old pair sat
  // at about 1.9:1 against its own background — legible as a shape, not as
  // words. These land near 5:1.
  disabledText: dark ? 'rgba(255,255,255,0.55)' : '#5F5F6B',

  // Notes / activity feed (per-source bubble colours + type scale)
  noteIndent: '49px',
  noteTabSize: '0.8125rem',
  noteNameSize: '0.875rem',
  noteMetaSize: '0.6875rem',
  noteBodySize: '0.875rem',
  noteInfoBg: dark ? 'rgba(96,165,250,0.16)' : '#EBF3FE',
  noteInfoText: dark ? '#9EC5FE' : '#274D7A',
  noteHistoryBg: dark ? 'rgba(251,146,60,0.16)' : '#FBEBDC',
  noteHistoryText: dark ? '#F5C08A' : '#714806',
  noteDriverBg: dark ? 'rgba(52,211,153,0.16)' : '#E4F6EC',
  noteDriverText: dark ? '#8FE0B4' : '#1B5E3F',
  noteBubbleBg: dark ? 'rgba(148,163,184,0.16)' : '#ECEFF5',
  noteBubbleText: dark ? '#B6C0D0' : '#3B4457',
  noteAdminBg: dark ? 'rgba(244,114,182,0.16)' : '#FCE7F0',
  noteAdminText: dark ? '#F0A6C8' : '#8A2B5B',
  noteCustomerBg: dark ? 'rgba(167,139,250,0.16)' : '#F1ECFB',
  noteCustomerText: dark ? '#C9B7F5' : '#4B3B7A',
  noteShippingBg: dark ? 'rgba(45,212,191,0.16)' : '#E2F4F1',
  noteShippingText: dark ? '#8FE0D6' : '#0F544D',
  noteProductBg: dark ? 'rgba(96,165,250,0.18)' : '#E6F0FE',
  noteProductText: dark ? '#9FC7FA' : '#14508F',
  noteAlertBg: dark ? 'rgba(248,113,113,0.24)' : '#FBDCD9',
  noteAlertText: dark ? '#FF9E97' : '#A81C13',
  noteCartBg: dark ? 'rgba(120,128,145,0.20)' : '#DFE1E8',
  noteCartText: dark ? '#C6CBD6' : '#2F323B',
  noteAddressBg: dark ? 'rgba(129,140,248,0.16)' : '#E9EAFC',
  noteAddressText: dark ? '#B4BAF8' : '#333A8C',

  // Solid light plate behind brand logos — intentionally white in both
  // modes so wordmarks stay legible on a dark surface.
  logoPlate: '#FFFFFF',

  // The three shapes MUI has no component for. They ride on the palette as
  // ready-made `sx` so a page still gets everything from the theme:
  // `const T = useTheme().palette.dash` then `sx={T.card}`.

  // The shell renders every page inside a padded Container, so a page's own
  // surface has to bleed back out to the edges.
  page: {
    backgroundColor: dark ? '#171717' : '#F1F2F6',
    marginInline: { xs: '-16px', sm: '-24px' },
    marginBlockStart: '-32px',
    paddingInline: { xs: '16px', sm: '24px' },
    paddingBlock: '16px',
    paddingBlockStart: '28px',
    minHeight: 'calc(100vh - 48px)',
  },

  // Cards here are drawn with Box, not Paper, so `<Paper variant="card">`
  // cannot reach them. Prefer the variant in new code.
  card: {
    border: '1px solid',
    borderColor: surfaces(dark).line,
    borderRadius: CARD_RADIUS,
    backgroundColor: surfaces(dark).surface,
    boxShadow: surfaces(dark).cardShadow,
  },

  // A dense numeric grid. The zebra ties with both the hover and the selected
  // rule on specificity, so those are restated after it — otherwise a striped
  // row never looks hovered or selected.
  gridRow: {
    '&:nth-of-type(odd)': { backgroundColor: dark ? '#171717' : '#F1F2F6' },
    '&.MuiTableRow-hover:hover': {
      backgroundColor: alpha(ACCENT, ROW_HOVER_ALPHA),
    },
    '&.Mui-selected, &.Mui-selected:hover': {
      backgroundColor: alpha(ACCENT, ROW_SELECTED_ALPHA),
    },
  },
});

export type DashTokens = ReturnType<typeof dashTokens>;
